import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Col, Dropdown, Menu, Row } from "antd";
import { Fragment } from "react/jsx-runtime";
import { journalsQueries } from "@/entities/journal";
import {
  generateAddressTypeName,
  ProjectParticipantInfo,
} from "@/entities/project";
import { useValidateTitle } from "@/features/journal-title-validation";
import { ParticipantsInfiniteScroll } from "@/features/participants-infinite-scroll";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator } from "@/shared/lib/hooks";
import { fallbackString, formatDate } from "@/shared/utils";
import { useGetRepresentative } from "../../api";
import {
  useCreateParticipant,
  useDeleteParticipant,
  useTitleAmendmentMode,
} from "../../model";
import { CollapsibleSection } from "../collapsible-section";
import { createValidationErrorsGetter } from "../journals-title/model/get-validation-errors-for-section";
import { TecCustomerControls, TecCustomerRepresentative } from "./ui";

export const TecCustomerCompanyTableNew = () => {
  const { titleId } = useCoordinator();
  const { projectId = "" } = useCoordinator();
  const { data: titleJournal } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );
  const { isLoading } = useQuery(journalsQueries.getTitleJournal({ titleId }));
  const titleStatus =
    titleJournal?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !titleJournal?.mainJournal?.titleJournal?.versionNumber;
  const { isNewAmendment } = useTitleAmendmentMode();
  const canAddData = titleStatus || isNewAmendment;

  const { errorList } = useValidateTitle();

  const { representative: techCustomer, isFetching: isTechCustomerFetching } =
    useGetRepresentative("technicalCustomer");

  const validationErrors = useMemo(() => {
    if (!techCustomer?.company) {
      return [];
    }
    return errorList.filter((error) => [7, 8, 15, 26, 27].includes(error.id));
  }, [errorList, techCustomer]);

  const getValidationErrorsForSection = useMemo(
    () => createValidationErrorsGetter(errorList),
    [errorList],
  );

  const { mutate: createParticipant, isPending: createParticipantPending } =
    useCreateParticipant({ titleId });

  const { mutate: deleteParticipant, isPending: deleteParticipantPending } =
    useDeleteParticipant({ titleId });

  const onPartySelect = async (participant: ProjectParticipantInfo) => {
    if (participant) {
      createParticipant({
        participantType: "technicalCustomer",
        projectParticipantId: participant.projectParticipantId,
        companyId: participant.company.companyId,
      });
    }
  };

  const handleDelete = () => {
    deleteParticipant({
      projectParticipantId: techCustomer?.projectParticipantId ?? "",
      participantType: "technicalCustomer",
    });
  };

  const menu = (
    <Menu>
      <Menu.Item key="delete" icon={<BasketIcon />} onClick={handleDelete}>
        Удалить
      </Menu.Item>
    </Menu>
  );

  const company = techCustomer?.company;

  return (
    <CollapsibleSection
      title="Технический заказчик"
      spinning={isLoading || deleteParticipantPending || isTechCustomerFetching}
      validationErrors={validationErrors}
      sectionId="tec-customer-company"
      getValidationErrorsForSection={getValidationErrorsForSection}
      isEmpty={!techCustomer?.company}
    >
      <>
        {company ? (
          <div>
            <div className="journal-title-block-header">
              <div className="journal-title-company-name">
                <div>
                  {fallbackString(company.fullName || company.shortName)}
                </div>
              </div>
              {titleStatus && !isNewAmendment ? (
                <Can I="edit" a="title_journals">
                  <Dropdown overlay={menu} trigger={["click"]}>
                    <span className="journal-title-menu-trigger">
                      <MoreOneIcon width={24} height={24} />
                    </span>
                  </Dropdown>
                </Can>
              ) : null}
            </div>
            <Row gutter={[16, 16]}>
              <Fragment>
                <Col span={8} style={{ color: "#888888" }}>
                  ИНН
                </Col>
                <Col span={16} className="table-info-content">
                  {fallbackString(company.inn)}
                </Col>
              </Fragment>
              <Fragment>
                <Col span={8} style={{ color: "#888888" }}>
                  КПП
                </Col>
                <Col span={16} className="table-info-content">
                  {fallbackString(company.kpp)}
                </Col>
              </Fragment>
              <Fragment>
                <Col span={8} style={{ color: "#888888" }}>
                  ОГРН
                </Col>
                <Col span={16} className="table-info-content">
                  {fallbackString(company.ogrn)}
                </Col>
              </Fragment>
              <Fragment>
                <Col span={8} style={{ color: "#888888" }}>
                  Контакты
                </Col>
                <Col span={16} className="table-info-content">
                  {fallbackString(
                    `${company.contacts?.phone || ""}${company.contacts?.email || ""}`,
                  )}
                </Col>
              </Fragment>
              <Fragment>
                <Col span={8} style={{ color: "#888888" }}>
                  Адрес
                </Col>
                <Col span={16} className="table-info-content">
                  {fallbackString(
                    generateAddressTypeName(
                      // @ts-expect-error fullAddress отсутствует в openapi типе адреса компании
                      company.addresses?.legal?.fullAddress,
                    ),
                  )}
                </Col>
              </Fragment>
            </Row>
            {techCustomer?.company?.sro?.map((item) => {
              return (
                <div key={item.sroId}>
                  <div className="journal-title-sro-title">
                    СРО{" "}
                    {item.sroType === "BUILDING"
                      ? "строительство"
                      : "проектирование"}
                  </div>
                  <Row gutter={[16, 16]}>
                    <Fragment>
                      <Col span={8} style={{ color: "#888888" }}>
                        Номер
                      </Col>
                      <Col span={16}>{fallbackString(item.sroNumber)}</Col>
                    </Fragment>
                    <Fragment>
                      <Col span={8} style={{ color: "#888888" }}>
                        Дата
                      </Col>
                      <Col span={16}>{formatDate(item.sroDate)}</Col>
                    </Fragment>
                    <Fragment>
                      <Col span={8} style={{ color: "#888888" }}>
                        Выдано
                      </Col>
                      <Col span={16}>{fallbackString(item.sroIsserName)}</Col>
                    </Fragment>
                    <Fragment>
                      <Col span={8} style={{ color: "#888888" }}>
                        ИНН
                      </Col>
                      <Col span={16}>{fallbackString(item.sroIsserInn)}</Col>
                    </Fragment>
                    <Fragment>
                      <Col span={8} style={{ color: "#888888" }}>
                        ОГРН
                      </Col>
                      <Col span={16}>{fallbackString(item.sroIsserOgrn)}</Col>
                    </Fragment>
                  </Row>
                </div>
              );
            })}
            {techCustomer?.company?.sro?.filter(
              (item) => item.sroType === "BUILDING",
            ).length === 0 && (
              <div>
                <div className="journal-title-sro-title">СРО строительство</div>
                <Row gutter={[16, 16]}>
                  <Fragment>
                    <Col span={8} style={{ color: "#888888" }}>
                      Номер
                    </Col>
                    <Col span={16}>-</Col>
                  </Fragment>
                  <Fragment>
                    <Col span={8} style={{ color: "#888888" }}>
                      Дата
                    </Col>
                    <Col span={16}>-</Col>
                  </Fragment>
                  <Fragment>
                    <Col span={8} style={{ color: "#888888" }}>
                      Выдано
                    </Col>
                    <Col span={16}>-</Col>
                  </Fragment>
                  <Fragment>
                    <Col span={8} style={{ color: "#888888" }}>
                      ИНН
                    </Col>
                    <Col span={16}>-</Col>
                  </Fragment>
                  <Fragment>
                    <Col span={8} style={{ color: "#888888" }}>
                      ОГРН
                    </Col>
                    <Col span={16}>-</Col>
                  </Fragment>
                </Row>
              </div>
            )}
          </div>
        ) : (
          <>
            {canAddData ? (
              <Can I="edit" a="title_journals">
                <div>
                  <div className="journal-title-org-heading">Организация</div>
                  <ParticipantsInfiniteScroll
                    projectId={projectId}
                    onPartySelect={onPartySelect}
                    isLoading={createParticipantPending}
                  />
                </div>
              </Can>
            ) : null}
          </>
        )}
      </>
      <TecCustomerRepresentative />
      <TecCustomerControls />
    </CollapsibleSection>
  );
};
